/** In production, set VITE_API_BASE to your deployed API URL (e.g. https://your-api.railway.app). Locally, /api is proxied to the Node server. */
import type { RadioConfig } from '../utils/radioConfig';

export type { RadioConfig };

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '') || '/api';

const TOKEN_KEY = 'admin_token';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredToken(): string | null {
  return getToken();
}

export async function login(username: string, password: string): Promise<{ token: string; username: string; role: 'admin' | 'user' }> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Login failed');
  }
  return res.json();
}

export async function register(username: string, password: string): Promise<{ token: string; username: string; role: 'user' }> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Registration failed');
  }
  return res.json();
}

export async function checkUsernameAvailability(username: string): Promise<{
  available: boolean;
  code?: string;
  message?: string;
}> {
  const q = new URLSearchParams({ username });
  const res = await fetch(`${API_BASE}/auth/check-username?${q.toString()}`);
  const data = (await res.json().catch(() => ({}))) as { available?: boolean; code?: string; message?: string };
  if (!res.ok) {
    return {
      available: false,
      code: data.code || 'request_failed',
      message: data.message || 'Could not validate username',
    };
  }
  return {
    available: !!data.available,
    code: data.code,
    message: data.message,
  };
}

export interface ProfileApi {
  user_id?: number | null;
  /** DB role for listener accounts; use with JWT for moderator UI when token is stale */
  app_role?: 'user' | 'admin' | 'superuser' | null;
  plan_slug?: string | null;
  username: string;
  avatar_url: string | null;
  display_name?: string | null;
  email?: string | null;
  location?: string | null;
  bio?: string | null;
  token?: string;
  created_at?: string | null;
  stats?: {
    totalPlaylists: number;
    totalTracks: number;
    totalListeningTimeMinutes: number;
    followers: number;
    following: number;
    likedTracks: number;
    likedPlaylists: number;
  };
  activity?: ActivityFeedItem[];
}

export async function getProfile(): Promise<ProfileApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load profile');
  return res.json();
}

export async function updateProfileAvatar(avatar_url: string | null): Promise<ProfileApi> {
  return updateProfile({ avatar_url });
}

export async function updateProfile(data: {
  username?: string | null;
  avatar_url?: string | null;
  display_name?: string | null;
  email?: string | null;
  location?: string | null;
  bio?: string | null;
}): Promise<ProfileApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update profile');
  const profile = (await res.json()) as ProfileApi;
  // Username changes return a rotated token tied to the new username.
  if (profile.token) {
    localStorage.setItem(TOKEN_KEY, profile.token);
  }
  if (profile.username) {
    localStorage.setItem('admin_username', profile.username);
  }
  return profile;
}

export async function getPlaylists(): Promise<PlaylistApi[]> {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}/playlists`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error('Failed to load playlists');
  return res.json();
}

/** Single playlist (respects visibility: public / followers / private). */
export async function getPlaylist(id: number): Promise<PlaylistApi> {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}/playlists/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || 'Failed to load playlist');
  }
  return res.json();
}

export async function createPlaylist(data: { name: string; description?: string; coverImage?: string; visibility?: 'public' | 'followers' | 'private' }): Promise<PlaylistApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create playlist');
  return res.json();
}

export async function updatePlaylist(
  id: number,
  data: { name?: string; description?: string; coverImage?: string; trackIds?: number[]; visibility?: 'public' | 'followers' | 'private' }
): Promise<PlaylistApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update playlist');
  return res.json();
}

export async function deletePlaylist(id: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to delete playlist');
}

export async function getTracks(): Promise<TrackApi[]> {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}/tracks`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error('Failed to load tracks');
  return res.json();
}

export async function createTrack(data: {
  title: string;
  artist: string;
  album: string;
  duration: string;
  image: string;
  audio_url?: string;
}): Promise<TrackApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/tracks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create track');
  return res.json();
}

export async function updateTrack(
  id: number,
  data: {
    title: string;
    artist: string;
    album: string;
    duration: string;
    image: string;
    audio_url?: string | null;
  }
): Promise<TrackApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/tracks/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      title: data.title,
      artist: data.artist,
      album: data.album,
      duration: data.duration,
      image: data.image,
      audio_url: data.audio_url?.trim() ? data.audio_url.trim() : null,
    }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update track');
  return res.json();
}

export async function deleteTrack(id: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/tracks/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to delete track');
}

export interface AppUserApi {
  id: number;
  username: string;
  display_name: string;
  avatar_url: string | null;
  followers_count: number;
  is_following: boolean;
  is_online: boolean;
  last_seen_at: string | null;
  now_playing: string | null;
}

export async function searchUsers(params?: { search?: string; filter?: 'all' | 'following' | 'online' }): Promise<AppUserApi[]> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const q = new URLSearchParams();
  if (params?.search) q.set('search', params.search);
  if (params?.filter) q.set('filter', params.filter);
  const res = await fetch(`${API_BASE}/users${q.toString() ? `?${q.toString()}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load users');
  return res.json();
}

export interface PublicProfileApi {
  id: number;
  username: string;
  display_name: string;
  avatar_url: string | null;
  bio: string;
  location: string;
  created_at: string;
  followers_count: number;
  following_count: number;
  is_following: boolean;
  playlists: Array<{
    id: number;
    name: string;
    description: string;
    cover_image: string;
    created_at: string;
    visibility: string;
    track_count: number;
    likes_count: number;
  }>;
}

export async function getPublicProfile(username: string): Promise<PublicProfileApi> {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}/user-profile/${encodeURIComponent(username)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load profile');
  return res.json();
}

export async function followUser(userId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/users/${userId}/follow`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to follow user');
}

export async function unfollowUser(userId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/users/${userId}/follow`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to unfollow user');
}

export async function likeTrack(trackId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/tracks/${trackId}/like`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to like track');
}

export async function unlikeTrack(trackId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/tracks/${trackId}/like`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to unlike track');
}

export async function likePlaylist(playlistId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/like`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to like playlist');
}

export async function unlikePlaylist(playlistId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/like`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to unlike playlist');
}

export interface PlaylistCommentApi {
  id: number;
  body: string;
  created_at: string;
  edited_at?: string | null;
  edit_count?: number;
  /** True when the last edit was by a moderator (admin/superuser), not the comment author */
  last_edited_by_admin?: boolean;
  /** Shown when a moderator adds a note while editing someone else's comment */
  moderator_note?: string | null;
  user_id: number;
  username: string;
  display_name: string;
}

export async function getPlaylistComments(playlistId: number): Promise<PlaylistCommentApi[]> {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/comments`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load comments');
  return res.json();
}

export async function addPlaylistComment(playlistId: number, body: string): Promise<PlaylistCommentApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to add comment');
  return res.json();
}

export async function updatePlaylistComment(
  playlistId: number,
  commentId: number,
  body: string,
  options?: { moderator_note?: string | null }
): Promise<PlaylistCommentApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const payload: Record<string, unknown> = { body };
  if (options && Object.prototype.hasOwnProperty.call(options, 'moderator_note')) {
    payload.moderator_note = options.moderator_note;
  }
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/comments/${commentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update comment');
  return res.json();
}

export async function deletePlaylistComment(playlistId: number, commentId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/comments/${commentId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 204) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to delete comment');
}

export interface PlaylistCollaboratorApi {
  user_id: number;
  username: string;
  display_name: string;
  avatar_url: string | null;
  created_at: string;
}

export async function getPlaylistCollaborators(playlistId: number): Promise<PlaylistCollaboratorApi[]> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/collaborators`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load collaborators');
  return res.json();
}

export async function addPlaylistCollaborator(playlistId: number, userId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/playlists/${playlistId}/collaborators`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to add collaborator');
}

export interface ActivityFeedItem {
  id: number;
  activity_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
  actor_username?: string;
  actor_display_name?: string;
  target_username?: string;
  playlist_name?: string;
  track_title?: string;
}

export async function getFollowingFeed(): Promise<ActivityFeedItem[]> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/feed`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load feed');
  return res.json();
}

export async function reportNowPlaying(trackId: number | null): Promise<void> {
  const token = getStoredToken();
  if (!token) return;
  await fetch(`${API_BASE}/me/now-playing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ track_id: trackId }),
  });
}

export interface StoryApi {
  id: number;
  title: string;
  excerpt: string;
  body: string;
  cover_image: string | null;
  created_at: string;
  updated_at: string;
  is_published: boolean;
}

export interface StoryCommentApi {
  id: number;
  story_id: number;
  user_id: number;
  username: string;
  user_role?: string;
  is_moderator_comment?: boolean;
  body: string;
  created_at: string;
  edited_at?: string | null;
  edit_count?: number;
  /** True when the last edit was by a moderator (admin/superuser), not the comment author */
  last_edited_by_admin?: boolean;
  /** Shown when a moderator adds a note while editing someone else's comment */
  moderator_note?: string | null;
}

export interface StoryWithCommentsApi extends StoryApi {
  comments: StoryCommentApi[];
}

export async function getStories(): Promise<StoryApi[]> {
  const res = await fetch(`${API_BASE}/stories`);
  if (!res.ok) throw new Error('Failed to load stories');
  return res.json();
}

/** Admin: list all stories including unpublished */
export async function getStoriesForAdmin(): Promise<StoryApi[]> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/stories?all=1`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load stories');
  return res.json();
}

export async function getStory(id: number): Promise<StoryWithCommentsApi> {
  const res = await fetch(`${API_BASE}/stories/${id}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load story');
  return res.json();
}

export async function createStory(data: { title: string; excerpt?: string; body?: string; cover_image?: string; is_published?: boolean }): Promise<StoryApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/stories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create story');
  return res.json();
}

export async function updateStory(id: number, data: { title?: string; excerpt?: string; body?: string; cover_image?: string; is_published?: boolean }): Promise<StoryApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/stories/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update story');
  return res.json();
}

export async function deleteStory(id: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/stories/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to delete story');
}

export async function addStoryComment(storyId: number, body: string): Promise<StoryCommentApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/stories/${storyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to add comment');
  return res.json();
}

/** Moderator or author: delete a comment on a story */
export async function deleteStoryComment(storyId: number, commentId: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/stories/${storyId}/comments/${commentId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 204) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to delete comment');
}

/** Moderator or author: update a comment on a story. Pass `moderator_note` when a moderator edits someone else's comment. */
export async function updateStoryComment(
  storyId: number,
  commentId: number,
  body: string,
  options?: { moderator_note?: string | null }
): Promise<StoryCommentApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const payload: Record<string, unknown> = { body };
  if (options && Object.prototype.hasOwnProperty.call(options, 'moderator_note')) {
    payload.moderator_note = options.moderator_note;
  }
  const res = await fetch(`${API_BASE}/stories/${storyId}/comments/${commentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    let parsed: { error?: string } = {};
    try {
      parsed = JSON.parse(text) as { error?: string };
    } catch {
      /* not JSON (e.g. HTML error page) */
    }
    throw new Error(parsed.error || `Failed to update comment (HTTP ${res.status})`);
  }
  return res.json();
}

export interface NotificationApi {
  id: number;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type: string;
  icon: string;
}

export async function getNotifications(): Promise<NotificationApi[]> {
  const token = getStoredToken();
  if (!token) return [];
  const res = await fetch(`${API_BASE}/notifications`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const list = await res.json();
  return list.map((n: { created_at?: string; time?: string; [k: string]: unknown }) => ({
    ...n,
    time: n.time || n.created_at || '',
  }));
}

export async function markNotificationRead(id: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/notifications/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ read: true }),
  });
  if (!res.ok && res.status !== 204) throw new Error('Failed to update');
}

export async function markAllNotificationsRead(): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/notifications`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ markAllRead: true }),
  });
  if (!res.ok) throw new Error('Failed to update');
}

export async function deleteNotification(id: number): Promise<void> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/notifications/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 204) throw new Error('Failed to delete');
}

export interface UserApi {
  id: number;
  username: string;
  role: string;
  created_at: string;
}

export async function getAdminUsers(): Promise<UserApi[]> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/admin/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load users');
  return res.json();
}

export async function updateAdminUser(id: number, data: { role: string }): Promise<UserApi> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/admin/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update user');
  return res.json();
}

// ── Billing / Subscription ────────────────────────────────────────────────────

export interface Plan {
  id: number;
  slug: string;
  name: string;
  description: string;
  price_usd: number;
  price_ngn: number;
  features: string[];
  sort_order: number;
}

export interface SubscriptionApi {
  id: number;
  plan_slug: string;
  provider: 'stripe' | 'paystack' | 'manual';
  provider_subscription_id: string | null;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'paused';
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface PaymentApi {
  id: number;
  provider: string;
  provider_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

export interface BillingInfo {
  plan_slug: string;
  subscription: SubscriptionApi | null;
  recent_payments: PaymentApi[];
  stripe_customer_id: string | null;
  paystack_customer_code: string | null;
}

export async function getPlans(): Promise<Plan[]> {
  const res = await fetch(`${API_BASE}/billing/plans`);
  if (!res.ok) throw new Error('Failed to load plans');
  const data = await res.json();
  return data.plans;
}

export async function getBillingInfo(): Promise<BillingInfo> {
  const res = await fetch(`${API_BASE}/billing/me`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Failed to load billing info');
  }
  return res.json();
}

export async function createCheckout(planSlug: string, provider: 'stripe' | 'paystack'): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ plan_slug: planSlug, provider }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Checkout failed');
  }
  return res.json();
}

export async function openBillingPortal(): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/billing/portal`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Could not open billing portal');
  }
  return res.json();
}

export async function cancelSubscription(resume = false): Promise<{ message: string }> {
  const res = await fetch(`${API_BASE}/billing/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ resume }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Failed to update subscription');
  }
  return res.json();
}

export async function getPaymentHistory(page = 1, perPage = 20): Promise<{ payments: PaymentApi[]; total: number; page: number }> {
  const res = await fetch(`${API_BASE}/billing/history?page=${page}&per_page=${perPage}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error('Failed to load payment history');
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Radio (home page “live radio” card — stored in app_settings via API)

export async function getRadioConfig(): Promise<{ config: RadioConfig }> {
  const res = await fetch(`${API_BASE}/radio`);
  if (!res.ok) throw new Error('Failed to load radio configuration');
  return res.json();
}

export async function saveRadioConfig(config: RadioConfig): Promise<{ config: RadioConfig }> {
  const token = getStoredToken();
  if (!token) throw new Error('Unauthorized');
  const res = await fetch(`${API_BASE}/radio`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ config }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to save radio configuration');
  return res.json();
}

export interface TrackApi {
  id: number;
  title: string;
  artist: string;
  album: string;
  duration: string;
  image: string;
  audio_url?: string | null;
  created_at?: string;
  likes_count?: number;
  is_liked?: boolean;
}

export interface PlaylistApi {
  id: number;
  name: string;
  description: string;
  cover_image: string;
  coverImage?: string;
  created_at: string;
  createdAt?: string;
  created_by?: number;
  visibility?: 'public' | 'followers' | 'private';
  owner_username?: string | null;
  owner_avatar_url?: string | null;
  likes_count?: number;
  comments_count?: number;
  is_liked?: boolean;
  tracks: TrackApi[];
}
